// pipe: frontend wave (F6) — the PURE, prop-only storefront Cart/Checkout view
// (the Vitest-tested seam). Imports ONLY @affine/i18n, TYPE-ONLY imports from
// the cart + api services, the pure formatPrice/cartTotal helpers, and the css
// module — never the runtime service/auth/router/DI.
import { useI18n } from '@affine/i18n';

import { type CartLine, cartTotal } from '../services/cart';
import type { CheckoutReceipt } from '../services/storefront-api';
import { formatPrice } from './catalog-view';
import * as styles from './storefront.css';

export interface CartViewProps {
  lines: CartLine[];
  /** whether a buyer session exists (drives the checkout button label) */
  authed?: boolean;
  busy?: boolean;
  checkoutError?: string | null;
  /** a successful checkout's receipt — when set, the confirmation renders */
  receipt?: CheckoutReceipt | null;
  onRemove: (id: string) => void;
  onCheckout: () => void;
  onViewOrders?: () => void;
}

export function CartView(props: CartViewProps) {
  const t = useI18n();

  // Order placed — the confirmation screen. Payment is deliberately offline
  // (`manual_pending`): say so, plainly.
  if (props.receipt) {
    return (
      <div className={styles.body}>
        <h1 className={styles.title}>
          {t['com.affine.storefront.cart.confirmation.title']()}
        </h1>
        <div className={styles.successPanel} role="status">
          {t['com.affine.storefront.cart.confirmation.message']({
            orderNo: props.receipt.order_no,
          })}
        </div>
        {props.onViewOrders ? (
          <div className={styles.cartActions} style={{ marginBlockStart: 16 }}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={props.onViewOrders}
            >
              {t['com.affine.storefront.cart.confirmation.view-orders']()}
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  if (props.lines.length === 0) {
    return (
      <div className={styles.body}>
        <h1 className={styles.title}>
          {t['com.affine.storefront.cart.title']()}
        </h1>
        <div className={styles.statusPanel} role="status">
          {t['com.affine.storefront.cart.empty']()}
        </div>
      </div>
    );
  }

  const total = cartTotal(props.lines);
  // Display currency: single-currency carts show a formatted total (the server
  // enforces one currency per checkout anyway).
  const currency = props.lines[0]?.currency ?? null;

  return (
    <div className={styles.body}>
      <h1 className={styles.title}>{t['com.affine.storefront.cart.title']()}</h1>
      <div className={styles.cartList}>
        {props.lines.map(line => (
          <div key={line.id} className={styles.cartRow}>
            <span className={styles.cartHeadline}>
              {line.headline ?? line.id.slice(0, 8)}
            </span>
            <span className={styles.cartRowMeta}>
              <span>{formatPrice(line.ask_price, line.currency) ?? '—'}</span>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => props.onRemove(line.id)}
                disabled={Boolean(props.busy)}
              >
                {t['com.affine.storefront.cart.remove']()}
              </button>
            </span>
          </div>
        ))}
      </div>
      {total !== null ? (
        <div className={styles.cartTotalRow}>
          <span>{t['com.affine.storefront.cart.total']()}</span>
          <span>{formatPrice(total, currency)}</span>
        </div>
      ) : null}
      <div className={styles.cartActions}>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={props.onCheckout}
          disabled={Boolean(props.busy)}
        >
          {props.busy
            ? t['com.affine.storefront.cart.checkout-busy']()
            : props.authed
              ? t['com.affine.storefront.cart.checkout']()
              : t['com.affine.storefront.cart.sign-in-to-checkout']()}
        </button>
      </div>
      {props.checkoutError ? (
        <div className={styles.inlineError} role="alert">
          {t['com.affine.storefront.cart.checkout-error']()}
        </div>
      ) : null}
    </div>
  );
}
